import { alpha } from '@mui/material/styles';

/**
 * Centralizes component styling based on theme mode
 * @param {Object} theme - The MUI theme object
 * @returns {Object} Styled components objects
 */
export const getComponentStyles = (theme) => {
	const isDark = theme.palette.mode === 'dark';

	return {
		// Card components
		card: {
			borderRadius: { xs: 2, md: 3 },
			boxShadow: isDark
				? '0 4px 12px rgba(0,0,0,0.3)'
				: '0 4px 12px rgba(0,0,0,0.05)',
			backgroundColor: isDark
				? theme.palette.customColors?.drawerBackground
				: '#FFFFFF',
			color: isDark ? theme.palette.customColors?.drawerText : 'inherit',
			transition: theme.transitions.create(['background-color', 'box-shadow'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		cardContent: {
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#383E49',
			bgcolor: isDark
				? theme.palette.customColors?.drawerBackground
				: '#FFFFFF',
			transition: theme.transitions.create(['color', 'background-color'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Stat box with subtle vertical divider
		statBox: {
			textAlign: 'center',
			flex: '1',
			mb: { xs: 0.5, sm: 0.75 },
			position: 'relative',

			// Subtle vertical divider
			'&:not(:last-child)': {
				'&::after': {
					content: '""',
					position: 'absolute',
					right: 0,
					top: '25%',
					height: '50%',
					width: '1px',
					backgroundColor: (theme) =>
						theme.palette.mode === 'dark'
							? 'rgba(255, 255, 255, 0.08)'
							: 'rgba(0, 0, 0, 0.06)',
				},
			},

			color: (theme) =>
				theme.palette.mode === 'dark'
					? theme.palette.customColors?.cardText || '#E0E0E0'
					: undefined,
			'& .stat-label': {
				color: (theme) =>
					theme.palette.mode === 'dark'
						? theme.palette.customColors?.statLabelColor || '#A0AEC0'
						: undefined,
			},
		},

		// Card divider
		cardDivider: {
			borderColor: isDark
				? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)'
				: '#E5E7EB',
			transition: theme.transitions.create('border-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Summary icons
		summaryIcon: {
			width: '22px',
			height: '22px',
			marginBottom: '4px',
			objectFit: 'contain',
			filter: isDark ? 'brightness(1.2)' : 'none',
		},

		// Quantity value
		quantityValue: {
			fontWeight: 'bold',
			color: isDark ? '#4A99FF' : '#1570EF',
			fontSize: '1rem',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Categories value
		categoriesValue: {
			fontWeight: 'bold',
			color: isDark ? '#9D5CFF' : '#7C3AED',
			fontSize: '1rem',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Summary label
		summaryLabel: {
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#383E49',
			fontSize: '0.65rem',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Typography elements
		title: {
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: 'inherit',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		subtitle: {
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#6B7280',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Scrollable content
		scrollableContent: {
			display: 'flex',
			flexDirection: 'column',
			gap: 2,
			flexGrow: 1,
			overflowY: 'auto',
			'&::-webkit-scrollbar': { width: '4px' },
			'&::-webkit-scrollbar-track': {
				backgroundColor: isDark
					? 'rgba(255, 255, 255, 0.05)'
					: 'rgba(0, 0, 0, 0.05)',
				borderRadius: '4px',
			},
			'&::-webkit-scrollbar-thumb': {
				backgroundColor: isDark
					? 'rgba(255, 255, 255, 0.2)'
					: 'rgba(0,0,0,0.2)',
				borderRadius: '4px',
			},
			'&::-webkit-scrollbar-thumb:hover': {
				backgroundColor: isDark
					? 'rgba(255, 255, 255, 0.3)'
					: 'rgba(0, 0, 0, 0.3)',
			},
			transition: theme.transitions.create('background-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Item container
		itemContainer: {
			display: 'flex',
			alignItems: 'center',
			padding: 1,
			borderRadius: 1,
			'&:hover': {
				backgroundColor: isDark
					? 'rgba(255, 255, 255, 0.05)'
					: 'rgba(0, 0, 0, 0.02)',
			},
			transition: theme.transitions.create('background-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Item icon container
		itemIconContainer: {
			width: { xs: 32, sm: 40 },
			height: { xs: 32, sm: 40 },
			bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#F1F5F9',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: 1,
			mr: 2,
			flexShrink: 0,
			transition: theme.transitions.create('background-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Item icon
		itemIcon: {
			fontSize: { xs: '1rem', sm: '1.2rem' },
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#64748B',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Item name
		itemName: {
			fontWeight: 'medium',
			fontSize: { xs: '0.75rem', sm: 'inherit' },
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: 'inherit',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Item subtitle
		itemSubtitle: {
			fontSize: { xs: '0.7rem', sm: 'inherit' },
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#64748B',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Status indicators
		statusIndicator: {
			display: 'inline-flex',
			alignItems: 'center',
			gap: 0.5,
			px: { xs: 1, sm: 1.5 },
			py: 0.5,
			borderRadius: 1,
			fontSize: { xs: '0.65rem', sm: '0.75rem' },
			fontWeight: 'medium',
			flexShrink: 0,
			transition: theme.transitions.create(['color', 'background-color'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Out of stock indicator
		outOfStockIndicator: {
			bgcolor: isDark ? 'rgba(220, 38, 38, 0.2)' : '#FEECEB',
			color: isDark
				? theme.palette.customColors?.dangerColor || '#FF6B6B'
				: '#DC2626',
		},

		// Low stock indicator
		lowStockIndicator: {
			bgcolor: isDark ? 'rgba(217, 119, 6, 0.2)' : '#FEF3C7',
			color: isDark
				? theme.palette.customColors?.warningColor || '#FFA94D'
				: '#D97706',
		},

		// Status icon
		statusIcon: {
			fontSize: { xs: '0.7rem', sm: '0.875rem' },
		},

		// Empty state container
		emptyStateContainer: {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			height: '100%',
		},

		// Image styles
		itemImage: {
			width: '70%',
			height: '70%',
			objectFit: 'contain',
			filter: isDark ? 'brightness(1.2)' : 'none',
		},

		// Top selling stock card specific styles
		topSellingStockTitle: {
			fontSize: '0.9rem',
			fontWeight: 600,
			mb: 1,
		},

		topSellingStockHeader: {
			display: 'flex',
			py: 0.75,
			mb: 0.5,
			borderBottom: (theme) =>
				`1px solid ${
					theme.palette.mode === 'dark'
						? theme.palette.customColors?.cardBorder ||
							'rgba(255, 255, 255, 0.1)'
						: '#F1F5F9'
				}`,
			fontSize: '0.9rem',
			fontWeight: '500',
			color: (theme) =>
				theme.palette.mode === 'dark'
					? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
					: '#64748B',
		},

		topSellingStockItem: {
			display: 'flex',
			py: 0.75,
			alignItems: 'center',
			borderBottom: (theme, index, length) =>
				index < length - 1
					? `1px solid ${
							theme.palette.mode === 'dark'
								? 'rgba(255, 255, 255, 0.1)'
								: '#F1F5F9'
						}`
					: 'none',
			'&:hover': {
				bgcolor: (theme) =>
					theme.palette.mode === 'dark'
						? 'rgba(255, 255, 255, 0.05)'
						: 'rgba(0, 0, 0, 0.02)',
			},
		},

		topSellingStockProductName: {
			flex: 3,
			color: (theme) => (theme.palette.mode === 'dark' ? '#E0E0E0' : '#383E49'),
			fontWeight: '500',
			fontSize: '0.75rem',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},

		topSellingStockData: {
			fontSize: '0.75rem',
			color: (theme) =>
				theme.palette.mode === 'dark'
					? 'rgba(255, 255, 255, 0.7)'
					: 'rgba(0, 0, 0, 0.7)',
		},

		// Top container box
		containerBox: {
			flex: '0 0 auto',
			bgcolor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#FFFFFF',
			borderRadius: 2,
			boxShadow: isDark
				? '0 2px 10px rgba(0,0,0,0.2)'
				: '0 2px 10px rgba(0,0,0,0.05)',
			p: { xs: 1, sm: 1.5 },
			mb: 1,
			overflow: 'visible',
			transition: theme.transitions.create(['background-color', 'box-shadow'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Container title
		containerTitle: {
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#374151',
			mb: 0.5,
			fontSize: { xs: '0.8rem', sm: '0.9rem' },
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Metric container
		metricContainer: {
			display: 'flex',
			flexDirection: 'column',
			p: { xs: 0.75, sm: 1 },
			borderRight: {
				xs: 'none',
				sm: `1px solid ${
					isDark
						? theme.palette.customColors?.cardBorder ||
							'rgba(255, 255, 255, 0.1)'
						: '#E5E7EB'
				}`,
			},
			transition: theme.transitions.create('border-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Metric title
		metricTitle: {
			mb: 0.25,
			fontWeight: 'medium',
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#374151',
			fontSize: { xs: '0.7rem', sm: '0.75rem' },
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Warning metric title
		metricTitleWarning: {
			mb: 0.25,
			fontWeight: 'medium',
			color: isDark
				? theme.palette.customColors?.dangerColor || '#FF6B6B'
				: '#EF4444',
			fontSize: { xs: '0.7rem', sm: '0.75rem' },
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Metric value
		metricValue: {
			fontWeight: 'bold',
			fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
			lineHeight: 1.1,
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Blue variant
		metricValueBlue: {
			color: isDark
				? theme.palette.customColors?.brandColor || '#4A99FF'
				: '#1570EF',
		},

		// Purple variant
		metricValuePurple: {
			color: isDark ? '#9D85F2' : '#7C3AED',
		},

		// Standard variant
		metricValueStandard: {
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#374151',
		},

		// Metric caption
		metricCaption: {
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#374151',
			fontSize: { xs: '0.6rem', sm: '0.65rem' },
			lineHeight: 1.1,
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Danger caption
		metricCaptionDanger: {
			color: isDark
				? theme.palette.customColors?.dangerColor || '#FF6B6B'
				: '#EF4444',
			fontSize: { xs: '0.6rem', sm: '0.65rem' },
			lineHeight: 1.1,
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Chart styling
		chartContainer: {
			width: '100%',
			height: 220,
		},

		cartesianGrid: {
			strokeDasharray: '3 3',
			vertical: false,
			stroke: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e0e0e0',
		},

		axisTick: {
			fontSize: 10,
			fill: isDark ? '#A0AEC0' : '#666',
		},

		tooltipStyle: {
			backgroundColor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: 'white',
			border: isDark
				? `1px solid ${theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)'}`
				: 'none',
			borderRadius: 8,
			boxShadow: isDark
				? '0 4px 12px rgba(0,0,0,0.4)'
				: '0 4px 12px rgba(0,0,0,0.1)',
			fontSize: 12,
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: 'inherit',
		},

		// Form controls
		selectStyle: {
			fontSize: '0.75rem',
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#6B7280',
			'&::before, &::after': { display: 'none' },
			'& .MuiSelect-select': { pr: '24px', py: 0 },
			'& svg': {
				color: isDark
					? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
					: '#6B7280',
			},
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Stats summary styles

		// Stats card container
		statsSummaryCard: {
			bgcolor: isDark ? theme.palette.customColors?.cardBackground : '#ffffff',
			p: { xs: 1, sm: 2 },
			borderRadius: 1,
			boxShadow: isDark ? '0px 2px 4px rgba(0,0,0,0.4)' : 1,
			mb: 1,
			transition: theme.transitions.create(['background-color', 'box-shadow'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Stats container
		statsContainer: {
			display: 'flex',
			flexWrap: { xs: 'wrap', sm: 'nowrap' },
			color: isDark ? theme.palette.customColors?.cardSubtitle : '#5D6679',
			justifyContent: 'space-between',
			alignItems: 'stretch',
		},

		// Stats item (general)
		statsItem: {
			flex: { xs: '0 0 calc(50% - 8px)', sm: '1' },
			p: { xs: 1, sm: 1.5 },
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			minWidth: { xs: '90px', sm: 'auto' },
		},

		// Stats item with right border
		statsItemBorderRight: {
			borderRight: {
				xs: `1px solid ${isDark ? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)' : 'rgba(93, 102, 121, 0.2)'}`,
				sm: `1px solid ${isDark ? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)' : 'rgba(93, 102, 121, 0.3)'}`,
			},
		},

		// Stats item in second row (mobile)
		statsItemSecondRow: {
			mt: { xs: 1, sm: 0 },
		},

		// Stats label
		statsLabel: {
			fontSize: '0.75rem',
			textAlign: 'center',
			color: isDark ? theme.palette.customColors?.cardSubtitle : '#5D6679',
		},

		// Stats value
		statsValue: {
			fontWeight: 'bold',
			mb: { xs: 0, sm: 1 },
			fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
		},

		// Stats value variants
		statsValueWarning: {
			color: '#F78820',
		},

		statsValueDanger: {
			color: '#B91C1C',
		},

		// Revenue details container
		revenueDetailsContainer: {
			display: 'flex',
			flexDirection: { xs: 'column', sm: 'row' },
			justifyContent: { xs: 'center', sm: 'space-evenly' },
			alignItems: 'center',
			mb: { xs: 0, sm: 1 },
			width: '100%',
			gap: { xs: 0, sm: 1 },
		},

		// Revenue substat container
		revenueSubstatContainer: {
			display: 'flex',
			flexDirection: { xs: 'row', sm: 'column' },
			alignItems: { xs: 'center', sm: 'flex-end' },
			gap: { xs: 0.5, sm: 0 },
		},

		// Revenue label
		revenueLabel: {
			color: '#6B7280',
			fontSize: { xs: '0.6rem', sm: '0.65rem' },
			order: { xs: 1, sm: 2 },
			mt: { xs: 0, sm: -0.5 },
		},

		// Revenue value
		revenueValue: {
			fontWeight: 'bold',
			color: '#166534',
			fontSize: { xs: '0.75rem', sm: '0.9rem', md: '1rem' },
			order: { xs: 2, sm: 1 },
		},

		// Header section
		headerBox: {
			p: { xs: 1.5, sm: 2 },
			display: 'flex',
			flexDirection: { xs: 'column', sm: 'row' },
			justifyContent: 'space-between',
			alignItems: { xs: 'stretch', sm: 'center' },
			gap: { xs: 1, sm: 2 },
		},

		// Header title
		headerTitle: {
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#374151',
			fontSize: { xs: '1rem', sm: '1.25rem' },
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Button container
		actionButtonsContainer: {
			display: 'flex',
			flexDirection: 'row',
			flexWrap: 'wrap',
			gap: { xs: 0.5, sm: 1 },
			width: '100%',
			justifyContent: { xs: 'space-between', sm: 'flex-end' },
		},

		// Primary button
		primaryButton: {
			bgcolor: theme.palette.customColors?.brandColor || '#1570EF',
			'&:hover': {
				bgcolor: isDark ? 'rgba(74, 153, 255, 0.8)' : '#1565C0',
			},
			textTransform: 'none',
			fontSize: { xs: '0.7rem', sm: '0.8rem' },
			px: { xs: 1, sm: 2 },
			py: { xs: 0.5, sm: 0.75 },
			minWidth: { xs: 'auto', sm: '100px' },
			flex: { xs: '1 1 auto', sm: '0 0 auto' },
			maxWidth: { xs: 'calc(50% - 4px)', sm: 'none' },
			transition: theme.transitions.create(['background-color'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Outlined brand button
		outlinedBrandButton: {
			color: theme.palette.customColors?.brandColor || '#1570EF',
			borderColor: theme.palette.customColors?.brandColor || '#1570EF',
			'&:hover': {
				bgcolor: isDark ? 'rgba(74, 153, 255, 0.1)' : '#EFF6FF',
				borderColor: isDark
					? theme.palette.customColors?.brandColor || '#4A99FF'
					: '#1565C0',
			},
			textTransform: 'none',
			fontSize: { xs: '0.7rem', sm: '0.8rem' },
			px: { xs: 1, sm: 2 },
			py: { xs: 0.5, sm: 0.75 },
			minWidth: { xs: 'auto', sm: '100px' },
			flex: { xs: '1 1 auto', sm: '0 0 auto' },
			maxWidth: { xs: 'calc(50% - 4px)', sm: 'none' },
			transition: theme.transitions.create(
				['color', 'border-color', 'background-color'],
				{
					duration: theme.transitions.duration.standard,
				},
			),
		},

		// Outlined success button
		outlinedSuccessButton: {
			color: isDark
				? theme.palette.customColors?.successColor || '#4ADE80'
				: '#16A34A',
			borderColor: isDark
				? theme.palette.customColors?.successColor || '#4ADE80'
				: '#16A34A',
			'&:hover': {
				bgcolor: isDark ? 'rgba(74, 222, 128, 0.1)' : '#F0FDF4',
				borderColor: isDark ? 'rgba(74, 222, 128, 0.8)' : '#15803D',
			},
			textTransform: 'none',
			fontSize: { xs: '0.7rem', sm: '0.8rem' },
			px: { xs: 1, sm: 2 },
			py: { xs: 0.5, sm: 0.75 },
			minWidth: { xs: 'auto', sm: '100px' },
			flex: { xs: '1 1 auto', sm: '0 0 auto' },
			maxWidth: { xs: 'calc(50% - 4px)', sm: 'none' },
			transition: theme.transitions.create(
				['color', 'border-color', 'background-color'],
				{
					duration: theme.transitions.duration.standard,
				},
			),
		},

		// Badge in button
		badgeIndicator: {
			ml: 1,
			width: 20,
			height: 20,
			borderRadius: '50%',
			backgroundColor: theme.palette.customColors?.brandColor || '#1570EF',
			color: 'white',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			fontSize: '0.7rem',
			transition: theme.transitions.create('background-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Dialog paper
		dialogPaper: {
			width: '100%',
			maxWidth: 400,
			maxHeight: '80vh',
			backgroundColor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#FFFFFF',
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: 'inherit',
		},

		// Overview card
		overviewCard: {
			flex: { xs: '1', md: '1' },
			bgcolor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#ffffff',
			p: { xs: 1.5, sm: 2 },
			borderRadius: 1,
			boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.2)' : 1,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center', // Center content vertically
			minHeight: { xs: '180px', md: 'auto' },
			height: '100%',
			transition: theme.transitions.create(['background-color', 'box-shadow'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		statsRow: {
			display: 'flex',
			flexWrap: 'wrap',
			justifyContent: 'space-between',
			mb: 1,
			mt: 1, // Add a bit more top margin for centering
		},

		secondaryStatsRow: {
			display: 'flex',
			flexWrap: 'wrap',
			justifyContent: 'space-between',
			mt: 1, // Add a bit more top margin for centering
		},

		// Secondary stat box
		secondaryStatBox: {
			textAlign: 'center',
			flex: { xs: '0 0 50%', sm: '0 0 50%' },
			mb: { xs: 0.5, sm: 0.75 },
			px: { xs: 0.25, sm: 0.5 },
		},

		// Stat value
		statValue: {
			fontWeight: 'bold',
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#1F2937',
			fontSize: { xs: '0.9rem', sm: '1rem' },
			lineHeight: 1.2,
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Secondary stat value (smaller)
		secondaryStatValue: {
			fontWeight: 'bold',
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#1F2937',
			fontSize: { xs: '0.8rem', sm: '0.85rem' },
			lineHeight: 1.2,
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Growth stat value with conditional colors
		growthValue: (isPositive) => ({
			fontWeight: 'bold',
			color: isPositive
				? isDark
					? theme.palette.customColors?.successColor || '#4ADE80'
					: '#16A34A'
				: isDark
					? theme.palette.customColors?.dangerColor || '#FF6B6B'
					: '#DC2626',
			fontSize: { xs: '0.8rem', sm: '0.85rem' },
			lineHeight: 1.2,
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		}),

		// Stat caption
		statCaption: {
			fontSize: { xs: '0.55rem', sm: '0.6rem' },
			lineHeight: 1.1,
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#5D6679',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Secondary stat caption (smaller)
		secondaryStatCaption: {
			fontSize: { xs: '0.5rem', sm: '0.55rem' },
			lineHeight: 1.1,
			display: 'block',
			whiteSpace: 'nowrap',
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#5D6679',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Overview divider
		overviewDivider: {
			my: { xs: 0.25, sm: 0.5 },
			bgcolor: isDark ? 'rgba(255, 255, 255, 0.5)' : '#5D6679',
			height: '1px',
			opacity: isDark ? 0.1 : 0.2,
			transition: theme.transitions.create(['background-color', 'opacity'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Category card
		categoryCard: {
			flex: { xs: '1', md: '1' },
			bgcolor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#ffffff',
			p: { xs: 1.5, sm: 2 },
			borderRadius: 1,
			boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.2)' : 1,
			display: 'flex',
			flexDirection: 'column',
			height: '100%',
			transition: theme.transitions.create(['background-color', 'box-shadow'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Category title
		categoryTitle: {
			mb: 2,
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#5D6679',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Add this style for category headers
		categoryHeader: {
			display: 'flex',
			py: 1,
			px: 1,
			alignItems: 'center',
			justifyContent: 'space-between',
			borderBottom: `1px solid ${
				isDark
					? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)'
					: '#E5E7EB'
			}`,
			mb: 0.5,
		},

		categoryHeaderText: {
			fontSize: '0.75rem',
			fontWeight: 500,
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#64748B',
		},

		// Ensure category growth text is visible on all devices
		categoryGrowthText: {
			color: isDark
				? theme.palette.customColors?.successColor || '#4ADE80'
				: '#10b981',
			fontSize: '0.75rem',
			display: 'block',
			textAlign: 'right',
			whiteSpace: 'nowrap',
		},

		// Category item row
		categoryItem: {
			display: 'flex',
			py: 1.5,
			px: 1,
			transition: theme.transitions.create('border-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Category item border
		categoryItemBorder: (isLast) => ({
			borderBottom: !isLast
				? `1px solid ${
						isDark
							? theme.palette.customColors?.cardBorder ||
								'rgba(255, 255, 255, 0.1)'
							: '#E5E7EB'
					}`
				: 'none',
		}),

		// Category name text
		categoryNameText: {
			flex: 2,
			fontWeight: 500,
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#383E49',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Category total text
		categoryTotalText: {
			flex: 1,
			textAlign: 'right',
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#383E49',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Main scroll container
		mainScrollContainer: {
			flexGrow: 1,
			overflow: 'auto',
			p: { xs: 1, sm: 2 },
			pt: 0,
			'&::-webkit-scrollbar': { width: '4px' },
			'&::-webkit-scrollbar-thumb': {
				backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
				borderRadius: '4px',
			},
			height: '100%',
		},

		// Table header cell
		tableHeaderCell: {
			fontWeight: 'bold',
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#4B5563',
		},

		// Table body style
		tableBody: {
			'& .MuiTableCell-body': {
				fontWeight: 'medium',
				color: isDark
					? theme.palette.customColors?.cardText || '#E0E0E0'
					: '#4B5563',
				transition: theme.transitions.create('color', {
					duration: theme.transitions.duration.standard,
				}),
			},
		},

		// Table row hover
		tableRowHover: {
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB',
			},
			transition: theme.transitions.create('background-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Stock status indicator
		stockStatusIndicator: (status) => {
			let config = {
				'Out of Stock': {
					bgcolor: isDark ? 'rgba(255, 107, 107, 0.2)' : '#FEECEB',
					color: isDark
						? theme.palette.customColors?.dangerColor || '#FF6B6B'
						: '#DC2626',
				},
				'Low Stock': {
					bgcolor: isDark ? 'rgba(255, 169, 77, 0.2)' : '#FEF3C7',
					color: isDark
						? theme.palette.customColors?.warningColor || '#FFA94D'
						: '#D97706',
				},
				'In Stock': {
					bgcolor: isDark ? 'rgba(74, 222, 128, 0.2)' : '#DCFCE7',
					color: isDark
						? theme.palette.customColors?.successColor || '#4ADE80'
						: '#16A34A',
				},
			};

			return {
				display: 'inline-flex',
				alignItems: 'center',
				gap: 0.5,
				px: 1.5,
				py: 0.5,
				borderRadius: 1,
				fontSize: '0.75rem',
				fontWeight: 'medium',
				bgcolor: config[status]?.bgcolor || config['In Stock'].bgcolor,
				color: config[status]?.color || config['In Stock'].color,
				transition: theme.transitions.create(['background-color', 'color'], {
					duration: theme.transitions.duration.standard,
				}),
			};
		},

		// Action buttons
		actionButton: {
			brandColor: {
				color: theme.palette.customColors?.brandColor || '#1570EF',
				'&:hover': {
					bgcolor: isDark ? 'rgba(74, 153, 255, 0.1)' : '#EFF6FF',
				},
			},
			danger: {
				color: isDark
					? theme.palette.customColors?.dangerColor || '#FF6B6B'
					: '#EF4444',
				'&:hover': {
					bgcolor: isDark ? 'rgba(255, 107, 107, 0.1)' : '#FEF2F2',
				},
			},
		},

		// Mobile card styles
		mobileCard: {
			border: `1px solid ${
				isDark
					? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)'
					: '#E5E7EB'
			}`,
			borderRadius: 1,
			mb: 1.5,
			bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
			position: 'relative',
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB',
			},
			transition: theme.transitions.create(
				['background-color', 'border-color'],
				{
					duration: theme.transitions.duration.standard,
				},
			),
		},

		// Serial number badge
		serialNumberBadge: {
			position: 'absolute',
			top: 0,
			left: 0,
			bgcolor: theme.palette.customColors?.brandColor || '#1570EF',
			color: 'white',
			width: 24,
			height: 24,
			borderBottomRightRadius: 8,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			fontSize: '0.75rem',
			fontWeight: 'bold',
			transition: theme.transitions.create('background-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Mobile card header
		mobileCardHeader: {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			borderBottom: `1px solid ${
				isDark
					? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)'
					: '#F3F4F6'
			}`,
			p: 1.5,
			pl: 3,
			transition: theme.transitions.create('border-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Mobile card title
		mobileCardTitle: {
			fontWeight: 'bold',
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#374151',
			fontSize: '0.9rem',
			mr: 1,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			maxWidth: '70%',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Mobile card content
		mobileCardContent: {
			p: 1.5,
			pt: 1,
			display: 'grid',
			gridTemplateColumns: '1fr 1fr',
			gap: 1,
		},

		// Mobile card footer
		mobileCardFooter: {
			display: 'flex',
			justifyContent: 'flex-end',
			borderTop: `1px solid ${
				isDark
					? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)'
					: '#F3F4F6'
			}`,
			p: 1,
			transition: theme.transitions.create('border-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Info label
		infoLabel: {
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#6B7280',
			fontSize: '0.7rem',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Info value
		infoValue: {
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#374151',
			fontSize: '0.8rem',
			fontWeight: 'medium',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Mobile status indicator
		mobileStatusIndicator: (status) => {
			let config = {
				'Out of Stock': {
					bgcolor: isDark ? 'rgba(255, 107, 107, 0.2)' : '#FEECEB',
					color: isDark
						? theme.palette.customColors?.dangerColor || '#FF6B6B'
						: '#DC2626',
				},
				'Low Stock': {
					bgcolor: isDark ? 'rgba(255, 169, 77, 0.2)' : '#FEF3C7',
					color: isDark
						? theme.palette.customColors?.warningColor || '#FFA94D'
						: '#D97706',
				},
				'In Stock': {
					bgcolor: isDark ? 'rgba(74, 222, 128, 0.2)' : '#DCFCE7',
					color: isDark
						? theme.palette.customColors?.successColor || '#4ADE80'
						: '#16A34A',
				},
			};

			return {
				display: 'inline-flex',
				alignItems: 'center',
				gap: 0.25,
				px: 1,
				py: 0.25,
				borderRadius: 0.5,
				fontSize: '0.65rem',
				fontWeight: 'medium',
				bgcolor: config[status]?.bgcolor || config['In Stock'].bgcolor,
				color: config[status]?.color || config['In Stock'].color,
				transition: theme.transitions.create(['background-color', 'color'], {
					duration: theme.transitions.duration.standard,
				}),
			};
		},

		// Loading container
		loadingContainer: {
			textAlign: 'center',
			py: 3,
		},

		// Chart card container
		chartCardContainer: {
			bgcolor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#ffffff',
			p: { xs: 1.5, sm: 2 },
			borderRadius: 1,
			boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.2)' : 1,
			mb: 1,
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#383E49',
			display: 'flex',
			flexDirection: 'column',
			minHeight: { xs: '250px', sm: '300px' },
			height: { xs: 'auto', md: '40%' },
			transition: theme.transitions.create(
				['background-color', 'box-shadow', 'color'],
				{
					duration: theme.transitions.duration.standard,
				},
			),
		},

		// Chart header
		chartHeader: {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			mb: 1,
			flexDirection: { xs: 'column', sm: 'row' },
		},

		// Chart title
		chartTitle: {
			mb: { xs: 1, sm: 0 },
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: 'inherit',
		},

		// Time period selector container
		timePeriodContainer: {
			display: 'flex',
			alignItems: 'center',
		},

		// Calendar icon
		calendarIcon: {
			fontSize: '0.9rem',
			mr: 0.5,
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#6B7280',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Time period selector
		timePeriodSelector: {
			fontSize: '0.75rem',
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#6B7280',
			'&::before, &::after': { display: 'none' },
			'& .MuiSelect-select': {
				pr: '24px',
				py: 0,
			},
			'& svg': {
				color: isDark
					? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
					: '#6B7280',
			},
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Time period menu
		timePeriodMenu: {
			bgcolor: isDark
				? theme.palette.customColors?.paperBackground || '#1E1E2E'
				: '#ffffff',
		},

		// Chart container with explicit height
		chartContainer: {
			flex: 1,
			width: '100%',
			height: { xs: '200px', sm: 'calc(100% - 30px)' },
			position: 'relative',
			overflow: 'visible',
		},

		// Cartesian grid
		chartGrid: {
			strokeDasharray: '3 3',
			vertical: false,
			stroke: isDark ? 'rgba(255, 255, 255, 0.1)' : '#F0F0F0',
		},

		// Chart axis
		chartAxis: {
			stroke: isDark ? '#A0AEC0' : '#666',
			tickLine: false,
		},

		// Chart axis tick
		chartAxisTick: {
			fontSize: '0.7rem',
			fill: isDark ? '#A0AEC0' : '#666',
		},

		// Chart tooltip
		chartTooltip: {
			backgroundColor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#fff',
			border: `1px solid ${
				isDark
					? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)'
					: '#ddd'
			}`,
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: 'inherit',
		},

		// Chart tooltip label
		chartTooltipLabel: {
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#333',
		},

		// Chart legend text
		chartLegendText: {
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: 'inherit',
		},

		// Revenue line
		revenueLine: {
			stroke: isDark ? '#6366F1' : '#4F46E5',
			strokeWidth: 2,
		},

		// Revenue dot
		revenueDot: {
			stroke: isDark ? '#6366F1' : '#4F46E5',
			strokeWidth: 2,
			r: 4,
			fill: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#fff',
		},

		// Profit line
		profitLine: {
			stroke: isDark
				? theme.palette.customColors?.successColor || '#4ADE80'
				: '#10B981',
			strokeWidth: 2,
		},

		// Profit dot
		profitDot: {
			stroke: isDark
				? theme.palette.customColors?.successColor || '#4ADE80'
				: '#10B981',
			strokeWidth: 2,
			r: 4,
			fill: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#fff',
		},

		// Product card
		productCard: {
			flex: 1,
			bgcolor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#ffffff',
			p: { xs: 1.5, sm: 2 },
			borderRadius: 1,
			boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.2)' : 1,
			height: 'auto',
			minHeight: 0,
			mb: 0,
			display: 'flex',
			flexDirection: 'column',
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#383E49',
			transition: theme.transitions.create(
				['background-color', 'box-shadow', 'color'],
				{
					duration: theme.transitions.duration.standard,
				},
			),
		},

		// Products data container with scrolling
		productsTableContainer: {
			flex: 1,
			overflowY: 'auto',
			maxHeight: {
				xs: '250px',
				sm: '240px',
			},

			// Custom scrollbar for webkit browsers
			'&::-webkit-scrollbar': {
				width: '6px',
			},
			'&::-webkit-scrollbar-track': {
				background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f1f1f1',
				borderRadius: '10px',
			},
			'&::-webkit-scrollbar-thumb': {
				background: isDark ? 'rgba(255, 255, 255, 0.2)' : '#c1c1c1',
				borderRadius: '10px',
				'&:hover': {
					background: isDark ? 'rgba(255, 255, 255, 0.3)' : '#a1a1a1',
				},
			},

			// Firefox scrollbar styling
			scrollbarWidth: 'thin',
			scrollbarColor: isDark
				? 'rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05)'
				: '#c1c1c1 #f1f1f1',
		},

		// Product table header
		productTableHeader: {
			display: { xs: 'none', md: 'flex' },
			mb: 1,
			px: 1.5,
			fontWeight: 500,
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#64748b',
			fontSize: '0.875rem',
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Column width classes
		colFlex1: { flex: 1 },
		colFlex2: { flex: 2 },
		colFlex3: { flex: 3 },
		colFlex15: { flex: 1.5 },

		// Alignment classes
		textLeft: { textAlign: 'left' },
		textCenter: { textAlign: 'center' },
		textRight: { textAlign: 'right' },

		// Product table row
		productTableRow: {
			display: { xs: 'none', md: 'flex' },
			alignItems: 'center',
			py: 1.5,
			px: 1.5,
			borderBottom: `1px solid ${
				isDark
					? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)'
					: '#E5E7EB'
			}`,
			fontSize: '0.875rem',
			transition: theme.transitions.create('border-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Text styles
		fontBold: { fontWeight: 500 },
		productTextColor: {
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#383E49',
		},
		productSuccessColor: {
			color: isDark
				? theme.palette.customColors?.successColor || '#4ADE80'
				: '#10b981',
		},

		// Product mobile row
		productMobileRow: {
			display: { xs: 'block', md: 'none' },
			py: 1.5,
			px: 1.5,
			borderBottom: `1px solid ${
				isDark
					? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)'
					: '#E5E7EB'
			}`,
			fontSize: '0.875rem',
			transition: theme.transitions.create('border-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Product mobile header
		productMobileHeader: {
			display: 'flex',
			justifyContent: 'space-between',
			mb: 1,
		},

		// Product mobile title
		productMobileTitle: {
			fontWeight: 500,
			fontSize: '0.9rem',
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: 'inherit',
		},

		// Product growth text
		productGrowthText: {
			color: isDark
				? theme.palette.customColors?.successColor || '#4ADE80'
				: '#10b981',
			fontSize: '0.75rem',
		},

		// Product mobile info container
		productMobileInfo: {
			display: 'flex',
			flexWrap: 'wrap',
			fontSize: '0.75rem',
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#6B7280',
		},

		// Product info item
		productInfoItem: {
			mr: 2,
			mb: 0.5,
		},

		// Product info label
		productInfoLabel: {
			fontWeight: 500,
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: 'inherit',
		},

		// Orders card styles

		// Orders container
		ordersCard: {
			flex: 1,
			bgcolor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#ffffff',
			p: { xs: 1, sm: 2 },
			borderRadius: 1,
			boxShadow: isDark ? '0px 2px 4px rgba(0,0,0,0.4)' : 1,
			minHeight: 0,
			display: 'flex',
			flexDirection: 'column',
			transition: theme.transitions.create(['background-color', 'box-shadow'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Orders header
		ordersHeader: {
			display: 'flex',
			justifyContent: 'space-between',
			mb: 2,
		},

		// Orders title
		ordersTitle: {
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#64748B',
			fontWeight: 'bold',
		},

		// Orders table header
		ordersTableHeader: {
			display: { xs: 'none', md: 'flex' },
			borderBottom: `1px solid ${isDark ? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)' : '#E5E7EB'}`,
			py: 1.5,
			px: 1,
			fontWeight: 500,
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#64748B',
			fontSize: '0.875rem',
			transition: theme.transitions.create(['border-color', 'color'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Orders table cell header
		ordersTableHeaderCell: {
			flex: 1,
			textAlign: 'center',
		},

		// Orders table body
		ordersTableBody: {
			flex: 1,
			overflow: 'auto',
		},

		// Orders empty state
		ordersEmptyState: {
			textAlign: 'center',
			py: 4,
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#6B7280',
		},

		// Order row desktop
		orderRowDesktop: {
			display: { xs: 'none', md: 'flex' },
			borderBottom: `1px solid ${isDark ? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)' : '#F3F4F6'}`,
			py: 1.5,
			px: 1,
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#374151',
			'&:hover': {
				bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB',
			},
			cursor: 'pointer',
			transition: theme.transitions.create(
				['background-color', 'border-color', 'color'],
				{
					duration: theme.transitions.duration.standard,
				},
			),
		},

		// Order serial number container
		orderSerialContainer: {
			width: '40px',
			display: 'flex',
			justifyContent: 'center',
			mr: 1,
		},

		// Order cell
		orderCell: {
			flex: 1,
			textAlign: 'center',
		},

		// Order ID cell
		orderIdCell: {
			flex: 1,
			textAlign: 'center',
			fontWeight: 500,
		},

		// Order status container
		orderStatusContainer: {
			flex: 1,
			textAlign: 'center',
		},

		// Order status badge
		orderStatusBadge: (status) => {
			const statusStyle = {
				Pending: {
					bgcolor: isDark ? 'rgba(245, 158, 11, 0.2)' : '#FEF3C7',
					color: isDark ? '#F59E0B' : '#B45309',
				},
				Processing: {
					bgcolor: isDark ? 'rgba(79, 70, 229, 0.2)' : '#EEF2FF',
					color: isDark ? '#6366F1' : '#4338CA',
				},
				Completed: {
					bgcolor: isDark ? 'rgba(16, 185, 129, 0.2)' : '#D1FAE5',
					color: isDark ? '#10B981' : '#047857',
				},
				Cancelled: {
					bgcolor: isDark ? 'rgba(239, 68, 68, 0.2)' : '#FEE2E2',
					color: isDark ? '#EF4444' : '#B91C1C',
				},
				Failed: {
					bgcolor: isDark ? 'rgba(239, 68, 68, 0.2)' : '#FEE2E2',
					color: isDark ? '#EF4444' : '#B91C1C',
				},
			};

			return {
				padding: '4px 12px',
				borderRadius: '12px',
				fontSize: '0.75rem',
				backgroundColor:
					statusStyle[status]?.bgcolor || statusStyle.Pending.bgcolor,
				color: statusStyle[status]?.color || statusStyle.Pending.color,
			};
		},

		// Order status dropdown
		orderStatusDropdown: {
			fontSize: '0.75rem',
			ml: 1,
			minWidth: 100,
			height: '28px',
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#383E49',
			'& .MuiOutlinedInput-notchedOutline': {
				borderColor: isDark
					? 'rgba(255, 255, 255, 0.23)'
					: 'rgba(0, 0, 0, 0.23)',
			},
			'&:hover .MuiOutlinedInput-notchedOutline': {
				borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
			},
		},

		// Mobile order card
		orderRowMobile: {
			display: { xs: 'block', md: 'none' },
			borderBottom: `1px solid ${isDark ? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)' : '#F3F4F6'}`,
			py: 2,
			px: 1.5,
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#374151',
			'&:hover': {
				bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB',
			},
			position: 'relative',
			cursor: 'pointer',
			transition: theme.transitions.create(
				['background-color', 'border-color', 'color'],
				{
					duration: theme.transitions.duration.standard,
				},
			),
		},

		// Mobile order serial badge
		orderSerialBadge: {
			position: 'absolute',
			top: 0,
			left: 0,
			bgcolor: theme.palette.customColors?.brandColor || '#1570EF',
			color: 'white',
			width: 24,
			height: 24,
			borderBottomRightRadius: 8,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			fontSize: '0.75rem',
			fontWeight: 'bold',
		},

		// Mobile order header
		orderMobileHeader: {
			display: 'flex',
			justifyContent: 'space-between',
			mb: 1.5,
			alignItems: 'center',
		},

		// Mobile order label
		orderMobileLabel: {
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#64748B',
		},

		// Mobile order status dropdown
		orderMobileDropdown: {
			fontSize: '0.75rem',
			height: '32px',
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#64748B',
			borderRadius: 1,
			'& .MuiOutlinedInput-notchedOutline': {
				borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : '#CBD5E1',
				borderWidth: '1px',
			},
			'&:hover .MuiOutlinedInput-notchedOutline': {
				borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : '#94A3B8',
			},
			'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
				borderColor: theme.palette.customColors?.brandColor || '#1570EF',
				borderWidth: '1px',
			},
		},

		// Payment status colors
		paymentFailedText: {
			color: isDark
				? theme.palette.customColors?.dangerColor || '#FF6B6B'
				: '#B91C1C',
		},

		paymentPendingText: {
			color: isDark
				? theme.palette.customColors?.warningColor || '#FFA94D'
				: '#854D0E',
		},

		// categoryItemsContainer

		categoryItemsContainer: {
			overflowY: 'auto',
			height: '92px',
			maxHeight: '92px',

			// Prevent growth beyond 2 items
			flexGrow: 0,
			flexShrink: 0,

			// Custom scrollbar for webkit browsers
			'&::-webkit-scrollbar': {
				width: '6px',
			},
			'&::-webkit-scrollbar-track': {
				background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f1f1f1',
				borderRadius: '10px',
			},
			'&::-webkit-scrollbar-thumb': {
				background: isDark ? 'rgba(255, 255, 255, 0.2)' : '#c1c1c1',
				borderRadius: '10px',
				'&:hover': {
					background: isDark ? 'rgba(255, 255, 255, 0.3)' : '#a1a1a1',
				},
			},
			// Firefox scrollbar styling
			scrollbarWidth: 'thin',
			scrollbarColor: isDark
				? 'rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05)'
				: '#c1c1c1 #f1f1f1',
		},

		// Make each category item a consistent height
		categoryItem: {
			display: 'flex',
			py: 1.25,
			px: 1,
			height: '46px', // Fixed height per item (including padding)
			boxSizing: 'border-box', // Include padding in height calculation
			alignItems: 'center',
			transition: theme.transitions.create('border-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Update the categoryCard style if needed
		categoryCard: {
			flex: { xs: '1', md: '1' },
			bgcolor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#ffffff',
			p: { xs: 1.5, sm: 2 },
			borderRadius: 1,
			boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.2)' : 1,
			display: 'flex',
			flexDirection: 'column',
			minHeight: { xs: '180px', md: 'auto' },
			transition: theme.transitions.create(['background-color', 'box-shadow'], {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Table container styles
		tableContainer: {
			overflow: 'auto',
			flexGrow: 1,
			height: 0,
			'&::-webkit-scrollbar': { width: '4px' },
			'&::-webkit-scrollbar-track': {
				backgroundColor: isDark
					? 'rgba(255, 255, 255, 0.05)'
					: 'rgba(0, 0, 0, 0.05)',
				borderRadius: '4px',
			},
			'&::-webkit-scrollbar-thumb': {
				backgroundColor: isDark
					? 'rgba(255, 255, 255, 0.2)'
					: 'rgba(0, 0, 0, 0.2)',
				borderRadius: '4px',
			},
			'&::-webkit-scrollbar-thumb:hover': {
				backgroundColor: isDark
					? 'rgba(255, 255, 255, 0.3)'
					: 'rgba(0, 0, 0, 0.3)',
			},
			transition: theme.transitions.create('background-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Table header row
		tableHeaderRow: {
			'& th': { border: 0 },
			borderBottom: `2px solid ${
				isDark
					? theme.palette.customColors?.cardBorder || 'rgba(255, 255, 255, 0.1)'
					: '#E5E7EB'
			}`,
			transition: theme.transitions.create('border-color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Table header cell
		tableHeaderCell: {
			fontWeight: 'medium',
			color: isDark
				? theme.palette.customColors?.cardSubtitle || '#A0AEC0'
				: '#6B7280',
			position: 'sticky',
			top: 0,
			bgcolor: isDark
				? theme.palette.customColors?.cardBackground || '#1E1E2E'
				: '#FFFFFF',
			zIndex: 1,
			fontSize: { xs: '0.75rem', sm: 'inherit' },
			transition: theme.transitions.create(
				['color', 'background-color', 'border-color'],
				{
					duration: theme.transitions.duration.standard,
				},
			),
		},

		// Table row
		tableRow: (isLastRow = false) => ({
			'& td': { border: 0 },
			borderBottom: isLastRow
				? 0
				: `1px solid ${
						isDark
							? theme.palette.customColors?.cardBorder ||
								'rgba(255, 255, 255, 0.1)'
							: '#F1F5F9'
					}`,
			transition: theme.transitions.create('border-color', {
				duration: theme.transitions.duration.standard,
			}),
			'&:hover': {
				backgroundColor: isDark
					? 'rgba(255, 255, 255, 0.05)'
					: 'rgba(0, 0, 0, 0.02)',
			},
		}),

		// Table cell
		tableCell: {
			fontWeight: 'medium',
			color: isDark
				? theme.palette.customColors?.cardText || '#E0E0E0'
				: '#6B7280',
			fontSize: { xs: '0.75rem', sm: 'inherit' },
			transition: theme.transitions.create('color', {
				duration: theme.transitions.duration.standard,
			}),
		},

		// Chart colors
		chartColors: {
			orders: {
				main: isDark ? '#4A99FF' : '#1570EF',
				gradient: {
					id: isDark ? 'ordersGradientDark' : 'ordersGradientLight',
					stops: isDark
						? [
								{ offset: '5%', color: '#4A99FF', opacity: 0.3 },
								{ offset: '95%', color: '#4A99FF', opacity: 0.1 },
							]
						: [
								{ offset: '5%', color: '#1570EF', opacity: 0.2 },
								{ offset: '95%', color: '#1570EF', opacity: 0.05 },
							],
				},
			},
			delivered: {
				main: isDark ? '#E6B47E' : '#DBA362',
				gradient: {
					id: isDark ? 'deliveredGradientDark' : 'deliveredGradientLight',
					stops: isDark
						? [
								{ offset: '5%', color: '#E6B47E', opacity: 0.4 },
								{ offset: '95%', color: '#D5B68E', opacity: 0.2 },
							]
						: [
								{ offset: '5%', color: '#DF9B2D', opacity: 0.3 },
								{ offset: '95%', color: '#C5A674', opacity: 0.1 },
							],
				},
			},
		},

		// Legend indicator dot
		legendDot: {
			width: 8,
			height: 8,
			borderRadius: '50%',
			marginRight: 1,
		},

		// Legend text
		legendText: {
			fontWeight: 500,
			fontSize: '0.75rem',
		},

		// Chart cursor
		chartCursor: {
			strokeDasharray: '3 3',
			stroke: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
		},

		// Chart line style
		chartLine: {
			strokeWidth: 1.5,
			dot: {
				r: 2.5,
				strokeWidth: 0,
			},
			activeDot: {
				r: 5,
				strokeWidth: 0,
			},
		},

		selectMenuProps: {
			PaperProps: {
				sx: {
					bgcolor: isDark
						? theme.palette.customColors?.paperBackground || '#1E1E2E'
						: '#ffffff',
				},
			},
		},

		// Gradients for charts
		gradients: {
			sales: {
				startColor: isDark ? '#8C84FF' : '#817AF3',
				midColor: isDark ? '#7D95FF' : '#74B0FA',
				endColor: isDark ? '#84D7FF' : '#79D0F1',
			},
			purchase: {
				startColor: isDark ? '#50B87F' : '#46A46C',
				midColor: isDark ? '#5ADA6A' : '#51CC5D',
				endColor: isDark ? '#62E670' : '#57DA65',
			},
		},
	};
};
